import React, { useState, useEffect, useRef } from "react";
import "../styles/InfoTooltip.css";

const tooltipContent = {
  "Audio Recorder": {
    interaction:
      "This recorder allows your students to record their audio responses to your question. Or, you can use it to record your own voice instead.",
    benefits:
      "Audio recording questions are excellent for assessing speaking skills, pronunciation, and oral production in language learning. They allow students to practice and demonstrate their verbal communication abilities, which is crucial for language acquisition and confidence building.",
  },
  "Audio Player": {
    interaction:
      "Students can listen to this audio clip and adjust playback settings like volume and speed. Soundwaves scaffold the audio, giving students a visual representation of pauses, stress and intonation.",
    benefits:
      "Use this to provide listening comprehension exercises, pronunciation models, or audio instructions. Students can practice their listening skills at their own pace, repeating sections as needed for better understanding.",
  },
  "Multiple Choice": {
    interaction: "Students select one correct answer from multiple options.",
    benefits:
      "Multiple choice questions enhance recall and recognition of learned material. They are effective for assessing factual knowledge and can cover a wide range of content quickly. According to a study by Butler and Roediger (2008), multiple choice tests can also serve as a learning tool, improving long-term retention of information.",
  },
  "Multiple Checkbox": {
    interaction:
      "Students select multiple correct answers from a list of options.",
    benefits:
      "Multiple Checkbox Questions require students to select more than one correct answer from a list of options, encouraging deeper cognitive processing. This format enhances critical thinking by pushing students to evaluate multiple possibilities rather than focusing on a single correct answer. The process of distinguishing between correct and incorrect answers reinforces comprehension of complex concepts (Martinez, 1999). Multiple checkbox questions are useful for students in upper-level education who need to develop higher-order thinking skills and apply knowledge in more nuanced ways, such as distinguishing between similar but subtly different options.",
  },
  "Fill in the Blanks": {
    interaction:
      "Students type in missing words or phrases in a sentence or paragraph.",
    benefits:
      "Fill In The Blanks questions are effective in encouraging active recall and strengthening memory connections by reinforcing context-based learning. They are valuable because they help students focus on recalling specific pieces of information without being prompted by answer choices, promoting deeper encoding of knowledge (Smith & Karpicke, 2014). Fill In The Blanks exercises are particularly effective for language learners or subjects that require mastery of key vocabulary and facts. This question type helps beginners and intermediate learners build foundational knowledge by recalling information in a structured context, aiding long-term retention.",
  },
  "Sentence Ordering": {
    interaction:
      "Students arrange given sentences or phrases into the correct order.",
    benefits:
      "Sentence ordering questions develop logical thinking and improve understanding of text structure and coherence. They are particularly useful for enhancing reading comprehension and writing skills. Research by Gernsbacher et al. (1990) suggests that the ability to order sentences correctly is strongly related to overall text comprehension abilities.",
  },
  "Spell the Word": {
    interaction: "Students click on letter pieces to spell a word correctly.",
    benefits:
      "Spelling exercises improve language proficiency and enhance written communication skills. They help students develop their orthographic knowledge and improve spelling skills by focusing on the specific order of letters in a word. This activity promotes phonological awareness, which is critical in reading development (Ehri, 2005). Spelling exercises are particularly beneficial for younger students or language learners who are still developing literacy skills. The cognitive process involved in encoding and retrieving the correct spelling of words also improves working memory and attention to detail, which is crucial in mastering new languages.",
  },
  Pairing: {
    interaction: "Students match items from the left to the right.",
    benefits:
      "Pairing questions assess understanding of relationships between concepts, enhancing cognitive connections and memory retention.",
  },
};

const InfoTooltip = ({ type }) => {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const content = tooltipContent[type] || {};

  return (
    <div className="info-tooltip-container" ref={tooltipRef}>
      <button
        className="info-icon"
        onClick={() => setIsVisible(!isVisible)}
        aria-label="Show information"
      ></button>
      {isVisible && (
        <div className="tooltip">
          <h3>{type}</h3>
          <h4>How the student interacts?</h4>
          <p>{content.interaction}</p>
          <h4>How it helps?</h4>
          <p>{content.benefits}</p>
        </div>
      )}
    </div>
  );
};

export default InfoTooltip;
